/* @jsx h */
import { defineComponent, h, PropType } from "vue";
import { Reservation, RestaurantCustomer } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { formatTime, splitShortDate } from "../../utils";
import { RESERVATION_STATUS_COLORS } from "./constants";
import { reservationStatusLabel } from "./customerDetailHelpers";

export const CustomerCRMReservationsSection = defineComponent({
  name: "CustomerCRMReservationsSection",
  props: {
    customer: { type: Object as PropType<RestaurantCustomer>, required: true },
    reservations: { type: Array as PropType<Reservation[]>, required: true },
    upcomingReservations: { type: Array as PropType<Reservation[]>, required: true },
    pastReservations: { type: Array as PropType<Reservation[]>, required: true },
    loading: { type: Boolean, required: true },
  },
  setup(props) {
    const { t } = useLocalization();

    const reservationRow = (r: any, upcoming: boolean) => (
      <div
        key={r.id}
        class={`${upcoming ? "bg-primary/5 border-primary/10" : "bg-base-100/40 border-base-content/5"} rounded-xl px-4 py-3 border flex items-center gap-3`}
      >
        <div
          class={`w-10 h-10 rounded-lg ${upcoming ? "bg-primary/10" : "bg-base-content/5"} flex flex-col items-center justify-center flex-shrink-0`}
        >
          {(() => {
            const [month, day] = splitShortDate(r.time || r.date);
            return (
              <>
                <span
                  class={`text-[10px] font-bold ${upcoming ? "text-primary" : "text-base-content/30"} leading-none`}
                >
                  {month}
                </span>
                <span class={`text-sm font-bold ${upcoming ? "text-primary" : "text-base-content/40"} leading-none`}>
                  {day}
                </span>
              </>
            );
          })()}
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class={`text-sm font-medium ${upcoming ? "text-accent" : "text-accent/70"}`}>
              {r.party_name || props.customer.name || t("reservations.customers.reservation")}
            </span>
            <span class={`badge badge-xs ${RESERVATION_STATUS_COLORS[r.status || ""] || "badge-ghost"}`}>
              {reservationStatusLabel(r.status, t)}
            </span>
          </div>
          <div
            class={`text-[11px] ${upcoming ? "text-base-content/35" : "text-base-content/25"} flex items-center gap-2 mt-0.5`}
          >
            {r.time && <span>{formatTime(r.time)}</span>}
            {r.party_size && <span>{t("reservations.customers.guests_count", [r.party_size])}</span>}
            {upcoming && r.table_name && <span>{r.table_name}</span>}
          </div>
        </div>
      </div>
    );

    return () => (
      <div class="max-w-4xl space-y-5">
        {props.loading ? (
          <div class="space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} class="rounded-xl bg-base-100/30 p-4 animate-pulse motion-reduce:animate-none">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-lg bg-base-content/8" />
                  <div class="flex-1 space-y-1.5">
                    <div class="h-3 w-28 bg-base-content/8 rounded" />
                    <div class="h-2.5 w-36 bg-base-content/5 rounded" />
                  </div>
                  <div class="h-4 w-16 bg-base-content/8 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : props.reservations.length > 0 ? (
          <>
            {props.upcomingReservations.length > 0 && (
              <div>
                <h3 class="text-[10px] font-semibold text-primary uppercase tracking-wider mb-2 flex items-center gap-1">
                  <span class="material-symbols-outlined text-[12px]">upcoming</span>
                  {t("reservations.customers.upcoming")}
                </h3>
                <div class="space-y-1.5">{props.upcomingReservations.map((r: any) => reservationRow(r, true))}</div>
              </div>
            )}
            {props.pastReservations.length > 0 && (
              <div>
                <h3 class="text-[10px] font-semibold text-base-content/30 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <span class="material-symbols-outlined text-[12px]">history</span>
                  {t("reservations.customers.past_reservations")}
                </h3>
                <div class="space-y-1.5">{props.pastReservations.map((r: any) => reservationRow(r, false))}</div>
              </div>
            )}
          </>
        ) : (
          <div class="flex items-center justify-center h-48">
            <div class="text-center space-y-2">
              <span class="material-symbols-outlined text-3xl text-base-content/15">event_seat</span>
              <p class="text-sm text-base-content/30">{t("reservations.customers.no_reservations_found")}</p>
            </div>
          </div>
        )}
      </div>
    );
  },
});
